/**
 * What the app says about itself outside its own window: the count in the tab
 * title, and the badge on an installed app's icon.
 *
 * Neither existed. Inside the app the chat tab had its count and GiviSnaps its
 * dot, but a Chrome tab in the background looked exactly the same with news
 * waiting as without, and an installed app's dock or taskbar icon never showed
 * anything at all.
 *
 * Pure, and free of React and Firebase, because the service worker imports it
 * too: it sets the badge when a push arrives with the app closed.
 */
import { APP_NAME } from '../../shared/constants';

/** What the badge should show: a number, a plain dot, or nothing. */
export type Badge = number | 'dot' | null;

/**
 * Unread chat messages are counted; a new GiviSnap is not, since the tab it
 * belongs to shows everyone at once and marks it with a dot rather than a
 * number. So a count when there is one, otherwise a dot for a GiviSnap,
 * otherwise nothing.
 */
export function badgeFor(unreadChat: number, newGiviSnap: boolean): Badge {
  if (unreadChat > 0) return unreadChat;
  return newGiviSnap ? 'dot' : null;
}

/**
 * The tab title. "(3) Givi Den" is what every other web app does with a count,
 * so it reads without explaining; a dot stands in when there is news but no
 * number to give it.
 */
export function titleFor(badge: Badge): string {
  if (badge === null) return APP_NAME;
  if (badge === 'dot') return `• ${APP_NAME}`;
  return `(${badge > 99 ? '99+' : badge}) ${APP_NAME}`;
}

/** The two methods, on either kind of navigator, where the browser has them. */
export interface BadgeHost {
  setAppBadge?: (contents?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
}

/**
 * Put the badge on the app's icon.
 *
 * Only an installed app has an icon to put it on, and only some browsers
 * support it -- Chrome and Edge on desktop do. Where it is missing, or the
 * browser turns the call down, there is nothing to recover and nothing the
 * person could do about it, so a refusal is ignored rather than surfaced.
 */
export async function showBadge(host: BadgeHost, badge: Badge): Promise<void> {
  try {
    if (badge === null) await host.clearAppBadge?.();
    // No argument is what asks for a plain dot.
    else if (badge === 'dot') await host.setAppBadge?.();
    else await host.setAppBadge?.(badge);
  } catch {
    // Not installed, not supported, or not allowed. The in-app marks and the
    // tab title still say the same thing.
  }
}

/**
 * Whether the service worker should put a dot on the icon for a push.
 *
 * Only with no window open. While one is, the page holds the real count and
 * keeps the badge to it; a dot set here would overwrite that number. With the
 * app closed there is no page to do it, and the next time it opens it replaces
 * the dot with the count or clears it.
 */
export function badgeOnPush(openWindows: number): boolean {
  return openWindows === 0;
}
